import type { ComponentType } from 'react'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import { selectors } from '~/redux/createRootReducer'
import { selectIsInitialUserCheckDone } from '~/account/model/selectors'
import { STEP_NAME } from '~/basket/model/constants'
import { links } from '~/links'
import { getBasketNavigation } from '~/basket/model/utils/navigation'
import { Basket } from '~/basket/model/reducers'
import { confirmStep as confirmStepAction } from '~/basket/model/session/actions'

import { BasketDebugger } from './BasketDebugger'

const progressStyles = {
  position: 'absolute',
  left: 'calc(50% - 20px)',
  top: 'calc(50% - 20px)',
}

/*
 * Here we bind basket steps to browser paths. If a user navigates to a step which is not yet allowed to be
 * seen, the effect will redirect the user to the current step
 */

export type StepNavigation = {
  previousSteps: string[]
  displayedStep: string
  nextSteps: string[]
  goToStep: (stepName: string, navigate: NavigateFunction) => void
  goToNextStep: () => void
  goToPreviousStep: () => void
}

export type InjectedBasketProps = {
  stepNavigation: StepNavigation
  isLoadingBasket: boolean
  basket: Basket
}

export const getPath = (stepName: string) => {
  switch (stepName) {
    case STEP_NAME.PACKAGE:
      return links.shipmentPackageLink()
    case STEP_NAME.RECEIVER:
      return links.shipmentReceiverLink()
    case STEP_NAME.SHIPPER:
      return links.shipmentShipperLink()
    case STEP_NAME.CUSTOMS:
      return links.shipmentCustomsLink()
    case STEP_NAME.OVERVIEW:
      return links.shipmentOverviewLink()
    case STEP_NAME.PARCELSHOP_PAYMENT:
      return links.shipmentParcelshopPaymentLink()
    case STEP_NAME.LABEL:
      // The labels page is not accesible by using the goToStep function. Use the shipmentLabelLink directly and supply an order ID
      return null
    default:
      throw new Error(`Unknown step: ${stepName}`)
  }
}

export const withBasket = (WrappedComponent: ComponentType<InjectedBasketProps>, displayedStepName: string) => {
  const WithBasket = () => {
    const navigate = useNavigate()
    const basket = useSelector(selectors.getBasket)
    const { isInitialUserCheckDone } = useSelector(selectIsInitialUserCheckDone)
    const isLoadingBasket = useSelector(selectors.isLoadingBasket)
    const basketError = useSelector(selectors.getBasketError)
    const dispatch = useDispatch()

    const confirmStep = () => dispatch(confirmStepAction(displayedStepName))

    const goToStep = useCallback(
      (stepName: string) => {
        const path = getPath(stepName)

        if (path) {
          navigate(path)
        }
      },
      [navigate]
    )

    useEffect(() => {
      if (basket) {
        const basketNavigation = getBasketNavigation(basket.steps)(displayedStepName)
        if (!basketNavigation.isDisplayedStepAllowed) {
          goToStep(basket.currentStep.name)
        }
      }
    }, [basket, goToStep])

    const basketNavigation = getBasketNavigation(basket?.steps || [])(displayedStepName)

    const stepNavigation: StepNavigation = {
      previousSteps: basketNavigation.previousSteps.map(({ name }) => name),
      displayedStep: displayedStepName,
      nextSteps: basketNavigation.nextSteps.map(({ name }) => name),
      goToStep: (stepName: string) => goToStep(stepName),
      goToNextStep: () => {
        const { nextStep } = basketNavigation
        if (nextStep) {
          confirmStep()
          goToStep(nextStep.name)
        }
      },
      goToPreviousStep: () => {
        const { previousStep } = basketNavigation
        if (previousStep) {
          goToStep(previousStep.name)
        }
      },
    }

    if (isInitialUserCheckDone && !isLoadingBasket && (!basket || basketError)) {
      throw new Error(basketError?.message || 'Could not load capabilities data')
    }

    return (
      <>
        {(!isInitialUserCheckDone || isLoadingBasket) && <CircularProgress sx={progressStyles} data-test="loader" />}
        {basket && (
          <WrappedComponent basket={basket} isLoadingBasket={isLoadingBasket} stepNavigation={stepNavigation} />
        )}
        <BasketDebugger />
      </>
    )
  }

  return WithBasket
}
